import React, { lazy } from 'react';
import { LinkDataType } from './internal-links';
import HomePageZoocasa from './themed-home-page-index/homepage-zoocasa';
import { RouteName } from 'components/dynamic-page/route-matchers';
import { HeadDataType } from 'components/head-data';
import {
  EXP_COMMERCIAL_CA_TENANT_ID,
  EXP_COMMERCIAL_TENANT_ID,
  EXP_IN_SERVICE_TENANT_ID,
  EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_US_TENANT_ID,
} from '@tenants';
import dynamic from 'next/dynamic';
import { PropsWithTenantId } from '@tenants/types';
import LoadingText from 'components/loading-text';
import { getTenantHomeConfig } from '@tenants/utils/configs';
import { getTenantById } from '@tenants';

import type { INamedContents } from 'pages';
import type { ListingCardData } from 'components/listing-card/listing-card';
import type { ExpListingCardDataWithCompliance } from 'types/listing-card';
import type { LastSearchUrlsResponse } from 'data/search-predictions';
import { LOCALE_HOME_PATHS } from 'types/locales';
import { ContentCard } from '@tenants/types';
import type { InsightCard } from './exp-components/insights-cards/InsightsCards';

export type HomePageProps = PropsWithTenantId<{
  isCrawler: boolean;
  featuredHomesMlsNums: INamedContents['featuredHomesMlsNums'];
  isNotificationBannerHidden: boolean;
  availableHomeListings: ListingCardData[];
  availableHomeListingsV2?: ExpListingCardDataWithCompliance[];
  useListingCardWithCompliance?: boolean;
  availableHomeListingsMeta: number;
  availableHomeListingsLocation: string;
  internalLinksData: LinkDataType[];
  isExpandedArea?: boolean;
  maintenance?: Maintenance;
  routeName?: RouteName;
  headerData?: HeadDataType;
  soldHomeListings?: ListingCardData[];
  featuredHomeListings?: ListingCardData[];
  showChatbotWelcome?: boolean;
  isSimpleChatbotExperimentEnabled?: boolean;
  homePageUrls?: LastSearchUrlsResponse;
  contentCardsWithDynamicLink?: ContentCard[];
  insightsCards?: InsightCard[];
}>;

export interface Maintenance {
  isMaintenance: boolean;
  maintenanceStartTime: string;
  maintenanceEndTime: string;
  maintenanceDate: string;
}

const HomePageExpCA = lazy(() => import('./themed-home-page-index/homepage-exp-ca'));
const HomePageExpUS = lazy(() => import('./themed-home-page-index/homepage-exp-us'));
const HomePageExpInService = lazy(() => import('./themed-home-page-index/homepage-exp-in-service'));
const HomePageExpCommercial = dynamic(() => import('./themed-home-page-index/homepage-exp-commercial'), {
  loading: () => <LoadingText />,
});
const HomePageExpCommercialCA = dynamic(() => import('./themed-home-page-index/homepage-exp-commercial-ca'), {
  loading: () => <LoadingText />,
});


export default function HomePage(props: HomePageProps) {

  // manually set the maintenance props for the homepage and change when needed -- values are currently dummy values
  const maintenance = { isMaintenance: false, maintenanceStartTime: 'Start time', maintenanceEndTime: 'End time', maintenanceDate: 'Date' };
  const dynamicContentCardLinks = {
    [LOCALE_HOME_PATHS.CONTENT_CARDS_CARDS_SOLD_NEARBY_TITLE]: props.homePageUrls?.soldPricesUrl,
  };
  const tenant = getTenantById(props.tenantId);
  const homeConfig = getTenantHomeConfig(tenant);
  const contentCardsWithDynamicLink = homeConfig?.contentCards?.cards?.map(card => {
    if (card.hasDynamicLink) {
      return {
        ...card,
        link: dynamicContentCardLinks[card.title],
      };
    } 
    return card;
  });

  switch (props.tenantId) {
    case EXP_REALTY_CA_TENANT_ID:
      return <HomePageExpCA availableHomeListings={props.availableHomeListings} availableHomeListingsV2={props.availableHomeListingsV2} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} homePageUrls={props.homePageUrls} contentCardsWithDynamicLink={contentCardsWithDynamicLink} />;
    case EXP_REALTY_US_TENANT_ID:
      return <HomePageExpUS availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} showChatbotWelcome={props.showChatbotWelcome} homePageUrls={props.homePageUrls} />;
    case EXP_IN_SERVICE_TENANT_ID:
      return <HomePageExpInService availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} homePageUrls={props.homePageUrls} />;
    case EXP_COMMERCIAL_TENANT_ID:
      return <HomePageExpCommercial insightsCards={props.insightsCards} />;
    case EXP_COMMERCIAL_CA_TENANT_ID:
      return <HomePageExpCommercialCA insightsCards={props.insightsCards} />;
    default:
      return <HomePageZoocasa availableHomeListings={props.availableHomeListings} availableHomeListingsMeta={props.availableHomeListingsMeta} availableHomeListingsLocation={props.availableHomeListingsLocation} internalLinksData={props.internalLinksData} isExpandedArea={props.isExpandedArea} maintenance={maintenance} routeName={props.routeName} tenantId={props.tenantId} headerData={props.headerData} isCrawler={props.isCrawler} featuredHomesMlsNums={props.featuredHomesMlsNums} isNotificationBannerHidden={props.isNotificationBannerHidden} soldHomeListings={props.soldHomeListings} featuredHomeListings={props.featuredHomeListings} homePageUrls={props.homePageUrls} />;
  }
}
